import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Td } from "../../features/web3/forex/components/table";
import { Button } from "../../components/ui/button";
import { formatDateTime } from "../../lib/format";
import { PartialTag } from "../forex/parts";
import { adminPost, useCan } from "./api";
import {
  AccountLink,
  BASE,
  ConnectionLink,
  Drawer,
  Money,
  PausedBy,
  PeriodLink,
  UserLink,
  linkCls,
  mono,
  muted,
  orDash,
} from "./parts";

// Lots arrive as exact decimal strings in LOTS here (not the user side's
// 1/10000 unit): shown as sent.

const owner = [{ label: "Login" }, { label: "Username" }, { label: "Kind" }];
const OwnerCells = ({ row }) => (
  <>
    <Td className={mono}>
      <AccountLink id={row.accountId}>{row.login}</AccountLink>
    </Td>
    <Td className={mono}>
      <UserLink username={row.username} />
    </Td>
    <Td className="text-xs">{row.kind}</Td>
  </>
);

// `withOwner` adds login/username/kind: on for the book-wide lists, off inside
// one account where they would repeat on every row.
export const positionColumns = (withOwner) => [
  { label: "Opened" },
  { label: "Position" },
  ...(withOwner ? owner : []),
  { label: "Symbol" },
  { label: "Type" },
  { label: "Lots", align: "right" },
  { label: "Open price", align: "right" },
  { label: "Current price", align: "right" },
  { label: "SL", align: "right" },
  { label: "TP", align: "right" },
  { label: "Profit", align: "right" },
  { label: "Swap", align: "right" },
];

export const positionRow = (withOwner) => (row) => (
  <tr key={row.position} className="hover:bg-white/[0.02]">
    <Td className={muted}>{formatDateTime(row.openTime)}</Td>
    <Td className={mono}>{row.position}</Td>
    {withOwner && <OwnerCells row={row} />}
    <Td className={mono}>{row.symbol}</Td>
    <Td className="text-xs">{row.type}</Td>
    <Td align="right" className={mono}>{row.lots}</Td>
    <Td align="right" className={mono}>{row.openPrice}</Td>
    <Td align="right" className={mono}>{row.currentPrice}</Td>
    <Td align="right" className={mono}>{orDash(row.stopLoss)}</Td>
    <Td align="right" className={mono}>{orDash(row.takeProfit)}</Td>
    <Td align="right"><Money value={row.profit} /></Td>
    <Td align="right"><Money value={row.swap} /></Td>
  </tr>
);

export const orderColumns = (withOwner) => [
  { label: "Placed" },
  { label: "Order" },
  ...(withOwner ? owner : []),
  { label: "Symbol" },
  { label: "Type" },
  { label: "Lots left / asked", align: "right" },
  { label: "Price", align: "right" },
  { label: "Trigger", align: "right" },
  { label: "SL", align: "right" },
  { label: "TP", align: "right" },
  { label: "Expires" },
  { label: "Comment" },
];

export const orderRow = (withOwner) => (row) => (
  <tr key={row.order} className="hover:bg-white/[0.02]">
    <Td className={muted}>{formatDateTime(row.setupTime)}</Td>
    <Td className={mono}>{row.order}</Td>
    {withOwner && <OwnerCells row={row} />}
    <Td className={mono}>{row.symbol}</Td>
    <Td className="whitespace-nowrap text-xs">{orDash(row.type)}</Td>
    <Td align="right" className={mono}>
      {row.lotsCurrent} / {row.lotsInitial}
    </Td>
    <Td align="right" className={mono}>{row.orderPrice}</Td>
    <Td align="right" className={mono}>{orDash(row.triggerPrice)}</Td>
    <Td align="right" className={mono}>{orDash(row.stopLoss)}</Td>
    <Td align="right" className={mono}>{orDash(row.takeProfit)}</Td>
    <Td className={muted}>{row.expirationTime ? formatDateTime(row.expirationTime) : "Until cancelled"}</Td>
    <Td className="text-xs">{orDash(row.comment)}</Td>
  </tr>
);

export const DEAL_COLUMNS = [
  { label: "Time" },
  { label: "Deal" },
  { label: "Symbol" },
  { label: "Action" },
  { label: "Entry" },
  { label: "Reason" },
  { label: "Lots", align: "right" },
  { label: "Price", align: "right" },
  { label: "Profit", align: "right" },
  { label: "Commission", align: "right" },
  { label: "Swap", align: "right" },
  { label: "Fee", align: "right" },
  { label: "Position" },
  { label: "Order" },
  { label: "Comment" },
];

export const dealRow = (row) => (
  <tr key={row.deal} className="hover:bg-white/[0.02]">
    <Td className={muted}>{formatDateTime(row.time)}</Td>
    <Td className={mono}>{row.deal}</Td>
    <Td className={mono}>{orDash(row.symbol)}</Td>
    <Td className="text-xs">{orDash(row.action)}</Td>
    <Td className="text-xs">{orDash(row.entry)}</Td>
    <Td className="text-xs">{orDash(row.reason)}</Td>
    <Td align="right" className={mono}>{row.lots}</Td>
    <Td align="right" className={mono}>{row.price}</Td>
    <Td align="right"><Money value={row.profit} /></Td>
    <Td align="right"><Money value={row.commission} /></Td>
    <Td align="right"><Money value={row.swap} /></Td>
    <Td align="right"><Money value={row.fee} /></Td>
    <Td className={mono}>{orDash(row.position)}</Td>
    <Td className={mono}>{orDash(row.order)}</Td>
    <Td className="text-xs">{orDash(row.comment)}</Td>
  </tr>
);

export const TRADE_COLUMNS = [
  { label: "Closed" },
  { label: "Position" },
  { label: "Symbol" },
  { label: "Type" },
  { label: "Lots closed", align: "right" },
  { label: "Lots entered", align: "right" },
  { label: "Open price", align: "right" },
  { label: "Close price", align: "right" },
  { label: "Opened" },
  { label: "Profit", align: "right" },
];

export const tradeRow = (row) => (
  <tr key={`${row.position}-${row.closeTime}`} className="hover:bg-white/[0.02]">
    <Td className={muted}>{formatDateTime(row.closeTime)}</Td>
    <Td className={mono}>{row.position}</Td>
    <Td className={mono}>{row.symbol}</Td>
    <Td className="text-xs">{row.type}</Td>
    {/* Unequal = closed in part; the rest is still an open position. */}
    <Td align="right" className={mono}>
      {row.lotsClosed}
      {/* Decimal strings: compare as numbers, never as text (014). */}
      {Number(row.lotsClosed) !== Number(row.lotsEntered) && <PartialTag />}
    </Td>
    <Td align="right" className={mono}>{row.lotsEntered}</Td>
    <Td align="right" className={mono}>{row.openPrice}</Td>
    <Td align="right" className={mono}>{row.closePrice}</Td>
    <Td className={muted}>{formatDateTime(row.openTime)}</Td>
    <Td align="right"><Money value={row.profit} /></Td>
  </tr>
);

export const ORDER_HISTORY_COLUMNS = [
  { label: "Done" },
  { label: "Order" },
  { label: "State" },
  { label: "Type" },
  { label: "Symbol" },
  { label: "Lots asked / left", align: "right" },
  { label: "Order price", align: "right" },
  { label: "Trigger price", align: "right" },
  { label: "Fill price", align: "right" },
  { label: "SL", align: "right" },
  { label: "TP", align: "right" },
  { label: "Reason" },
  { label: "Position" },
  { label: "Placed" },
  { label: "Expires" },
  { label: "Comment" },
];

export const orderHistoryRow = (row) => (
  <tr key={row.order} className="hover:bg-white/[0.02]">
    <Td className={muted}>{formatDateTime(row.doneTime)}</Td>
    <Td className={mono}>{row.order}</Td>
    <Td className="text-xs">{row.state}</Td>
    <Td className="whitespace-nowrap text-xs">{orDash(row.type)}</Td>
    <Td className={mono}>{row.symbol}</Td>
    <Td align="right" className={mono}>
      {row.lotsInitial} / {row.lotsCurrent}
    </Td>
    <Td align="right" className={mono}>{row.orderPrice}</Td>
    <Td align="right" className={mono}>{orDash(row.triggerPrice)}</Td>
    {/* The price the order actually got. */}
    <Td align="right" className={mono}>{orDash(row.fillPrice)}</Td>
    <Td align="right" className={mono}>{orDash(row.stopLoss)}</Td>
    <Td align="right" className={mono}>{orDash(row.takeProfit)}</Td>
    <Td className="text-xs">{orDash(row.reason)}</Td>
    <Td className={mono}>{orDash(row.position)}</Td>
    <Td className={muted}>{formatDateTime(row.setupTime)}</Td>
    {/* null = ran until cancelled. */}
    <Td className={muted}>{row.expirationTime ? formatDateTime(row.expirationTime) : "—"}</Td>
    <Td className="text-xs">{orDash(row.comment)}</Td>
  </tr>
);

export const DAILY_COLUMNS = [
  { label: "Day" },
  { label: "Balance", align: "right" },
  { label: "Credit", align: "right" },
  { label: "Equity", align: "right" },
  { label: "Floating", align: "right" },
  { label: "Margin", align: "right" },
  { label: "Free margin", align: "right" },
  { label: "Margin level", align: "right" },
  { label: "Realized", align: "right" },
  { label: "Balance moves", align: "right" },
  { label: "Credit moves", align: "right" },
  { label: "Charges", align: "right" },
  { label: "Swap", align: "right" },
  { label: "Commission", align: "right" },
];

export const dailyRow = (row) => (
  <tr key={row.date} className="hover:bg-white/[0.02]">
    <Td className={mono}>{row.date}</Td>
    <Td align="right"><Money value={row.balance} /></Td>
    <Td align="right"><Money value={row.credit} /></Td>
    <Td align="right"><Money value={row.equity} /></Td>
    <Td align="right"><Money value={row.floatingProfit} /></Td>
    <Td align="right"><Money value={row.margin} /></Td>
    <Td align="right"><Money value={row.freeMargin} /></Td>
    <Td align="right" className={mono}>{row.marginLevel == null ? orDash(null) : `${row.marginLevel}%`}</Td>
    <Td align="right"><Money value={row.realizedProfit} /></Td>
    <Td align="right"><Money value={row.balanceMoves} /></Td>
    <Td align="right"><Money value={row.creditMoves} /></Td>
    <Td align="right"><Money value={row.charges} /></Td>
    <Td align="right"><Money value={row.swap} /></Td>
    <Td align="right"><Money value={row.commission} /></Td>
  </tr>
);

export const PNL_COLUMNS = [
  { label: "Symbol" },
  { label: "Realized", align: "right" },
  { label: "Commission", align: "right" },
  { label: "Swap", align: "right" },
  { label: "Floating now", align: "right" },
];

export const pnlRow = (row) => (
  <tr key={row.symbol} className="hover:bg-white/[0.02]">
    <Td className={mono}>{row.symbol}</Td>
    <Td align="right"><Money value={row.realizedProfit} /></Td>
    <Td align="right"><Money value={row.commission} /></Td>
    <Td align="right"><Money value={row.swap} /></Td>
    <Td align="right"><Money value={row.floatingProfit} /></Td>
  </tr>
);

// ── Copy trading ─────────────────────────────────────────────

const MasterName = ({ m }) =>
  m?.id ? (
    <Link to={`${BASE}/masters/${m.id}`} className={linkCls}>
      {m.name ?? "Unnamed"}
    </Link>
  ) : (
    orDash(null)
  );

// v1.1.0: customer = a user's own master, signal = Ginox's own. Fixed once made.
export const MasterType = ({ type }) => (
  <span className={type === "signal" ? "text-sky-400" : undefined}>
    {type === "signal" ? "Signal" : "Customer"}
  </span>
);

// v1.6.0 (024): hidden = off the user-facing master list only. Still trades, keeps
// its copiers, and anyone with its id can connect and read its track record.
export const HiddenNote = ({ m }) =>
  m.hidden ? (
    <span className="block text-xs font-normal text-amber-400">
      Hidden {formatDateTime(m.hiddenAt)} by <PausedBy id={m.hiddenBy} />
    </span>
  ) : null;

// v1.27.0 (043): the owner removed its account, which stops the master for good. It
// keeps its record, name and login; username and tier read null.
export const StoppedNote = ({ m }) =>
  m.stoppedAt ? (
    <span className="block text-xs font-normal text-red-400">
      Stopped {formatDateTime(m.stoppedAt)}
    </span>
  ) : null;

// Hide asks first (024: say what it does not do); unhide does not. Both are safe
// to repeat, so a refetch of every forex-admin query is the whole follow-up.
export function HideToggle({ m, compact }) {
  const can = useCan();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const toggle = useMutation({
    mutationFn: (hide) => adminPost(`/masters/admin/${m.id}/${hide ? "hide" : "unhide"}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["forex-admin"] });
      setConfirming(false);
    },
  });
  const size = compact ? "mt-1 h-6 px-2 text-xs" : undefined;
  const errBox = "rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300";

  if (!can("masters.hide")) return null;
  return (
    <>
      {m.hidden ? (
        <Button className={size} disabled={toggle.isPending} onClick={() => toggle.mutate(false)}>
          {toggle.isPending ? "Unhiding…" : "Unhide"}
        </Button>
      ) : (
        <Button className={size} onClick={() => setConfirming(true)}>Hide</Button>
      )}
      {toggle.error && !confirming && <p className={`mt-1 ${errBox}`}>{toggle.error.message}</p>}
      <Drawer open={confirming} onClose={() => { setConfirming(false); toggle.reset(); }} title={`Hide ${m.name ?? "master"}`}>
        <p className="text-sm text-muted-foreground">
          Takes it off the master list for every user, its owner too. It keeps trading and keeps its copiers,
          and anyone with its id can still connect to it and read its track record.
        </p>
        {toggle.error && <p className={errBox}>{toggle.error.message}</p>}
        <Button disabled={toggle.isPending} onClick={() => toggle.mutate(true)}>
          {toggle.isPending ? "Hiding…" : "Hide master"}
        </Button>
      </Drawer>
    </>
  );
}

// v1.16.0 (030): whether copiers see the master's open positions. Read only for
// operators; null = a master made before the choice, which counts as not sharing.
export const SharesPositions = ({ v }) =>
  v == null ? <span className="text-xs text-muted-foreground">Not chosen</span> : v ? "Yes" : "No";

// v1.17.0 (031): the fixed USD a copier pays the master on each connect. Read only for
// operators; null = a master made before the fee, which charges nothing.
export const AccessFee = ({ v }) =>
  v == null ? (
    <span className="text-xs text-muted-foreground">Not chosen</span>
  ) : v === "off" ? (
    "Free"
  ) : (
    `${v} USD`
  );

// v1.23.0 (037): the provider the master, its copiers and its cover are on, and a
// switch to the other one while it runs in the background. Operators only.
export const PROVIDER = { LP_PRIME: "LP Prime", PCX: "PCX" };
const providerLabel = (p) => PROVIDER[p] ?? p;

// v1.24.0 (038): where a charge paid the master. WALLET charges have no credit
// ticket or credit deal; every charge before the release is MT5.
export const PAID_TO = { WALLET: "FOREX wallet", MT5: "Trading account" };
export const paidToLabel = (p) => PAID_TO[p] ?? p;

// v1.26.0 (041): how an access fee was paid. WALLET fees have no tickets; every fee
// before the release is MT5.
export const PAID_VIA = { WALLET: "FOREX wallet", MT5: "MT5" };
const paidViaLabel = (p) => PAID_VIA[p] ?? p;

export const ProviderCell = ({ m }) => (
  <>
    {providerLabel(m.provider)}
    {m.providerSwitch &&
      (m.providerSwitch.stuck ? (
        <span className="block text-xs font-medium text-red-400">Switch stuck, retrying</span>
      ) : (
        <span className="block text-xs text-amber-400">Switching to {providerLabel(m.providerSwitch.to)}…</span>
      ))}
  </>
);

// Starts the switch and nothing more: the 200 means "started", and the caller polls
// for the end. Every refusal leaves everything as it was; its sentence shows as is.
export function SwitchProvider({ m, onStarted }) {
  const can = useCan();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const target = m.provider === "PCX" ? "LP_PRIME" : "PCX";
  const start = useMutation({
    mutationFn: () => adminPost(`/masters/admin/${m.id}/provider`, { provider: target }),
    onSuccess: () => {
      onStarted?.(target);
      qc.invalidateQueries({ queryKey: ["forex-admin"] });
      setConfirming(false);
    },
  });

  // A stopped master is a 404 "No such standing master".
  if (!can("masters.switch_provider") || m.providerSwitch || m.stoppedAt) return null;
  return (
    <>
      <Button className="mt-2" onClick={() => setConfirming(true)}>Switch to {providerLabel(target)}</Button>
      <Drawer open={confirming} onClose={() => { setConfirming(false); start.reset(); }} title="Switch provider">
        <p className="text-sm text-muted-foreground">
          Switch {m.name ?? "this master"} and its {m.copierCount} {m.copierCount === 1 ? "copier" : "copiers"} to {providerLabel(target)}? Every account
          must have no open trade or order.
        </p>
        {start.error && (
          <p className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">{start.error.message}</p>
        )}
        <Button disabled={start.isPending} onClick={() => start.mutate()}>
          {start.isPending ? "Starting…" : `Switch to ${providerLabel(target)}`}
        </Button>
      </Drawer>
    </>
  );
}

export const MASTER_COLUMNS = [
  { label: "Master" },
  { label: "Type" },
  { label: "Owner" },
  { label: "Login" },
  { label: "Cover login" },
  { label: "Tier" },
  { label: "Cut", align: "right" },
  { label: "Schedule" },
  { label: "Sponsor", align: "right" },
  { label: "Min equity", align: "right" },
  { label: "Leverage", align: "right" },
  { label: "Copiers", align: "right" },
  { label: "Shares positions" },
  { label: "Access fee", align: "right" },
  { label: "Provider" },
  { label: "Earned", align: "right" },
  { label: "Terms changed" },
  { label: "Created" },
];

export const masterRow = (r) => (
  <tr key={r.id} className="hover:bg-white/[0.02]">
    <Td className="whitespace-nowrap font-medium">
      <MasterName m={r} />
      <HiddenNote m={r} />
      <StoppedNote m={r} />
      <div><HideToggle m={r} compact /></div>
    </Td>
    <Td className="text-xs"><MasterType type={r.type} /></Td>
    <Td className={mono}><UserLink username={r.username} /></Td>
    <Td className={mono}>{orDash(r.login)}</Td>
    {/* Null on a master made before covers. */}
    <Td className={mono}>{r.coverLogin ?? <span className="text-xs text-muted-foreground">No cover</span>}</Td>
    <Td className="text-xs">{orDash(r.tier)}</Td>
    <Td align="right" className={mono}>{r.cutPercent}%</Td>
    <Td className="whitespace-nowrap text-xs">{r.schedule.replaceAll("_", " ")}</Td>
    {/* v1.25.0: the part of each share paid to its BD sponsor; 0 is off. */}
    <Td align="right" className={mono}>{r.sponsorPercent}%</Td>
    <Td align="right" className={mono}>{r.minimumEquity}</Td>
    {/* Shared by the master, its cover and copiers. Null on a master made before it was stored. */}
    <Td align="right" className={mono}>{r.leverage == null ? orDash(null) : `1:${r.leverage}`}</Td>
    <Td align="right" className={mono}>
      <Link to={`${BASE}/connections?master=${r.id}`} className={linkCls}>{r.copierCount}</Link>
      {/* The owner disconnected all its copiers; nobody can connect until it ends. */}
      {r.disconnectingCopiers && (
        <span className="block text-xs font-normal text-amber-400">disconnecting all</span>
      )}
    </Td>
    <Td className="text-xs"><SharesPositions v={r.sharesPositions} /></Td>
    <Td align="right" className={mono}><AccessFee v={r.accessFee} /></Td>
    <Td className="whitespace-nowrap text-xs"><ProviderCell m={r} /></Td>
    <Td align="right"><Money value={r.earned} /></Td>
    {/* Null until the first terms change. */}
    <Td className={muted}>{r.termsChangedAt ? formatDateTime(r.termsChangedAt) : orDash(null)}</Td>
    <Td className={muted}>{formatDateTime(r.createdAt)}</Td>
  </tr>
);

export const CONNECTION_STATUS = {
  STANDING: ["Copying", "text-emerald-400"],
  DISCONNECTING: ["Disconnecting", "text-amber-400"],
  ENDED: ["Ended", "text-muted-foreground"],
};

// Python backend v1.0.0: nothing waits (waitingSince is always null), so the
// status is the API's own.
export const connectionStatus = (c) => CONNECTION_STATUS[c.status];

// Why an ended connection ended (v1.1.0). Null on one ended before causes were
// kept, which reads as plain "Ended".
export const END_CAUSE = {
  DISCONNECTED: "Disconnected",
  MASTER_DISCONNECTED: "Disconnected by master",
  COPIER_GONE: "Copier account gone",
  MASTER_GONE: "Master account gone",
  // v1.22.0 (036): the copier's equity stop disconnected it.
  EQUITY_STOPPED: "Equity stop reached",
};

// The side of the master's new trades a connection copies (v1.18.0). Read only: the
// copier sets it. Never null; connections made before it read BOTH.
export const COPY_DIRECTION = {
  BOTH: "Buys and sells",
  BUYS: "Buys only",
  SELLS: "Sells only",
};

// The copier's auto exit (v1.19.0): each limit null, a whole % of the copied trade's
// margin at open, or a USD amount with two decimals. Read only. 015's labels. The margin
// cap (v1.21.0) has the same shape, its % of equity.
export const limitText = (l) => (!l ? "None" : l.unit === "PERCENT" ? `${l.value}%` : `$${l.value}`);

// The equity floor (v1.22.0) is read live: null with a stop set means the trading server
// could not be read, so "Unavailable", not "None".
const usd = (v) => `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const equityFloorText = (c) =>
  !c.equityStop ? "None" : c.equityFloor == null ? "Unavailable" : usd(c.equityFloor);
export const usdText = (v) => (v == null ? "None" : usd(v));

// The margin cap (and the equity stop) in 015's own labels: "30% of equity", "$1,500.00".
export const marginCapText = (c) =>
  !c
    ? "None"
    : c.unit === "PERCENT"
      ? `${c.value}% of equity`
      : `$${Number(c.value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const CONNECTION_COLUMNS = [
  { label: "Started" },
  { label: "Status" },
  { label: "Copier" },
  { label: "Copier login" },
  { label: "Copier tier" },
  { label: "Master" },
  { label: "Master login" },
  { label: "Equity ratio", align: "right" },
  { label: "Direction" },
  { label: "Loss limit", align: "right" },
  { label: "Profit target", align: "right" },
  { label: "Margin cap", align: "right" },
  { label: "Position cap", align: "right" },
  { label: "Equity stop", align: "right" },
  { label: "Equity floor", align: "right" },
  { label: "Ended" },
  { label: "" },
];

export const connectionRow = (r) => {
  const [label, tone] = connectionStatus(r);
  return (
    <tr key={r.id} className="hover:bg-white/[0.02]">
      <Td className={muted}>{formatDateTime(r.createdAt)}</Td>
      <Td className={`text-xs font-medium ${tone}`}>
        {label}
        {END_CAUSE[r.endCause] && (
          <span className="block font-normal text-muted-foreground">{END_CAUSE[r.endCause]}</span>
        )}
      </Td>
      <Td className={mono}><UserLink username={r.username} /></Td>
      <Td className={mono}><AccountLink id={r.accountId}>{r.login}</AccountLink></Td>
      <Td className="text-xs">{orDash(r.tier)}</Td>
      <Td className="whitespace-nowrap"><MasterName m={r.master} /></Td>
      <Td className={mono}>{orDash(r.master?.login)}</Td>
      {/* The share of the copier's equity copying uses, a whole 1 to 100. */}
      <Td align="right" className={mono}>{r.equityRatio}%</Td>
      <Td className="text-xs whitespace-nowrap">{COPY_DIRECTION[r.copyDirection] ?? orDash(null)}</Td>
      <Td align="right" className={mono}>{limitText(r.lossLimit)}</Td>
      <Td align="right" className={mono}>{limitText(r.profitTarget)}</Td>
      {/* v1.21.0: the copier's exposure caps, read only. */}
      <Td align="right" className={`${mono} whitespace-nowrap`}>{marginCapText(r.marginCap)}</Td>
      <Td align="right" className={mono}>{r.positionCap ?? "None"}</Td>
      {/* v1.22.0: the copier's equity stop, read only, and its live floor. */}
      <Td align="right" className={`${mono} whitespace-nowrap`}>{marginCapText(r.equityStop)}</Td>
      <Td align="right" className={`${mono} whitespace-nowrap`}>{equityFloorText(r)}</Td>
      <Td className={muted}>{r.endedAt ? formatDateTime(r.endedAt) : orDash(null)}</Td>
      <Td className="text-xs">
        <Link to={`${BASE}/connections/${r.id}`} className={linkCls}>Open →</Link>
      </Td>
    </tr>
  );
};

// Charge state labels from 016 (the charges list and its drawer). A connection's
// own charges read 015's words instead: see CONNECTION_CHARGE_STATE.
export const CHARGE_STATE = {
  // v1.28.0 (042): the copier was debited; the share waits for releaseAt.
  AWAITING_RELEASE: ["Awaiting release", "text-cyan-400"],
  DONE: ["Done", "text-emerald-400"],
  UNKNOWN: ["Unknown", "text-amber-400"],
  FAILED: ["Failed", "text-red-400"],
};

// v1.25.0 (040): why a charge paid its master's sponsor nothing, and why a charge
// failed. The handoff's display labels.
export const NO_SPONSOR_REASON = {
  PERCENT_ZERO: "Sponsor percent 0",
  NO_BD_SPONSOR: "No BD sponsor",
  NOT_REFERRED: "Copier not referred",
  UNDER_A_CENT: "Under a cent",
};

export const FAILURE_REASON = {
  MT5_DEBIT_REFUSED: "MT5 refused the debit",
  WALLET_REFUSED: "Wallet refused",
  SPONSOR_LOOKUP_FAILED: "Sponsor lookup failed",
};
export const failureLabel = (f) => FAILURE_REASON[f.reason] ?? f.reason;

// The charges that recorded this sponsor, paid or not.
export const SponsorLink = ({ username }) =>
  username ? (
    <Link to={`${BASE}/settlements?tab=charges&sponsorUsername=${encodeURIComponent(username)}`} className={linkCls}>
      {username}
    </Link>
  ) : (
    orDash(null)
  );

export const CONNECTION_CHARGE_STATE = {
  AWAITING_RELEASE: ["Awaiting release", "text-cyan-400"],
  DONE: ["Paid", "text-emerald-400"],
  UNKNOWN: ["Checking", "text-amber-400"],
  FAILED: ["Failed", "text-red-400"],
};

export const chargeColumns = (withDirection) => [
  { label: "Created" },
  { label: "Ref" },
  { label: "State" },
  ...(withDirection ? [{ label: "Side" }] : []),
  { label: "Copier" },
  { label: "Master" },
  { label: "Master owner" },
  { label: "Master login" },
  { label: "Profit", align: "right" },
  { label: "Cut", align: "right" },
  { label: "Share", align: "right" },
  { label: "Master part", align: "right" },
  { label: "Sponsor" },
  { label: "Sponsor share", align: "right" },
  { label: "Paid to" },
  { label: "Debit" },
  { label: "Credit" },
  { label: "Refund" },
  { label: "Release at" },
  { label: "Released" },
  { label: "Connection" },
  { label: "Period" },
];

// v1.28.0 (042): a release that failed is retried every 10 minutes; flag it while it waits.
export const ReleaseRetry = ({ c }) =>
  c.state === "AWAITING_RELEASE" && c.releaseFailures > 0 ? (
    <span className="block font-normal text-amber-400">
      Retrying ({c.releaseFailures}), next {formatDateTime(c.nextReleaseAt)}
    </span>
  ) : null;

const timeOrDash = (t) => (t ? formatDateTime(t) : orDash(null));

// A charge moves one share from a copier to a master; onOpen shows its deals.
export const chargeRow = (onOpen, withDirection) => (r) => {
  const [label, tone] = CHARGE_STATE[r.state] ?? [r.state, "text-muted-foreground"];
  return (
    <tr key={r.id} onClick={(e) => onOpen && !e.target.closest("a") && onOpen(r.id)} className={onOpen ? "cursor-pointer hover:bg-white/[0.02]" : undefined}>
      <Td className={muted}>{formatDateTime(r.createdAt)}</Td>
      <Td className={mono}>{r.ref}</Td>
      <Td className={`text-xs font-medium ${tone}`}>
        {label}
        {r.failure && <span className="block font-normal">{failureLabel(r.failure)}</span>}
        <ReleaseRetry c={r} />
      </Td>
      {withDirection && <Td className="text-xs">{r.direction === "PAID" ? "Paid" : "Received"}</Td>}
      <Td className={mono}><UserLink username={r.copier.username} /> · {orDash(r.copier.login)}</Td>
      <Td className="whitespace-nowrap"><MasterName m={r.master} /></Td>
      <Td className={mono}><UserLink username={r.master.username} /></Td>
      <Td className={mono}>{orDash(r.master.login)}</Td>
      <Td align="right"><Money value={r.profit} /></Td>
      <Td align="right" className={mono}>{r.cutPercent}%</Td>
      <Td align="right"><Money value={r.share} /></Td>
      {/* v1.25.0: share = masterPart + sponsorShare. */}
      <Td align="right"><Money value={r.masterPart} /></Td>
      <Td className={mono}><SponsorLink username={r.sponsorUsername} /></Td>
      <Td align="right">
        <Money value={r.sponsorShare} />
        {r.noSponsorShareReason && (
          <span className="block text-xs text-muted-foreground">{NO_SPONSOR_REASON[r.noSponsorShareReason] ?? r.noSponsorShareReason}</span>
        )}
      </Td>
      <Td className="whitespace-nowrap text-xs">{paidToLabel(r.paidTo)}</Td>
      <Td className={mono}>{orDash(r.debitTicket)}</Td>
      <Td className={mono}>{orDash(r.creditTicket)}</Td>
      {/* The copier's refund after the master's credit was refused. */}
      <Td className={mono}>{orDash(r.refundTicket)}</Td>
      <Td className={muted}>{timeOrDash(r.releaseAt)}</Td>
      <Td className={muted}>{timeOrDash(r.releasedAt)}</Td>
      <Td className={mono}><ConnectionLink id={r.connectionId} /></Td>
      <Td className={mono}><PeriodLink id={r.periodId} /></Td>
    </tr>
  );
};

// v1.17.0 (016/031): one access fee, a copier's fixed payment to a master on connect.
// Same states as a charge. No detail route: the row carries everything stored.
export const ACCESS_FEE_COLUMNS = [
  { label: "Created" },
  { label: "Ref" },
  { label: "State" },
  { label: "Copier" },
  { label: "Master" },
  { label: "Master owner" },
  { label: "Master login" },
  { label: "Amount", align: "right" },
  { label: "Paid via" },
  { label: "Transaction" },
  { label: "Debit" },
  { label: "Credit" },
  { label: "Refund" },
  { label: "Connection" },
];

export const accessFeeRow = (r) => {
  const [label, tone] = CHARGE_STATE[r.state] ?? [r.state, "text-muted-foreground"];
  return (
    <tr key={r.id} className="hover:bg-white/[0.02]">
      <Td className={muted}>{formatDateTime(r.createdAt)}</Td>
      <Td className={mono}>{r.ref}</Td>
      <Td className={`text-xs font-medium ${tone}`}>{label}</Td>
      <Td className={mono}><UserLink username={r.copier.username} /> · {orDash(r.copier.login)}</Td>
      <Td className="whitespace-nowrap"><MasterName m={r.master} /></Td>
      <Td className={mono}><UserLink username={r.master.username} /></Td>
      <Td className={mono}>{orDash(r.master.login)}</Td>
      <Td align="right"><Money value={r.amount} /></Td>
      <Td className="whitespace-nowrap text-xs">{paidViaLabel(r.paidVia)}</Td>
      {/* The dashboard's id, fee-<id>, for its ledger. Null on MT5 and until known. */}
      <Td className="break-all font-mono text-xs">{orDash(r.tranxId)}</Td>
      {/* A wallet fee has no tickets. */}
      <Td className={mono}>{orDash(r.debitTicket)}</Td>
      <Td className={mono}>{orDash(r.creditTicket)}</Td>
      {/* The copier's refund after the master's credit was refused; such a fee is FAILED. */}
      <Td className={mono}>{orDash(r.refundTicket)}</Td>
      <Td className={mono}>
        {r.connectionId ? (
          <ConnectionLink id={r.connectionId} />
        ) : r.state === "DONE" ? (
          // Paid, then the connect failed: support refunds it by hand.
          <span className="text-xs text-amber-400">No connection</span>
        ) : (
          orDash(null)
        )}
      </Td>
    </tr>
  );
};
